import { useEffect, useState, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, Users, LogOut, Menu, X, MessageSquare,
  ChevronLeft, UserCog, Layers, Edit2, BarChart3, Settings, FileText
} from "lucide-react";
import { Button } from "@/components/ui/button";
import ProfileModal from "@/components/ProfileModal";
import NotificationBell from "@/components/NotificationBell";
import apiClient, { authApi } from "@/utils/api";

const AdminLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);

  const fetchProfile = useCallback(async () => {
    try {
      const res = await authApi.getProfile();
      setUser(res.data);
      localStorage.setItem("admin_user", JSON.stringify(res.data));
    } catch (error) {
      // If not authenticated, redirect to login
      if (error.response?.status === 401) {
        navigate("/panel-gestion-8k2m/acceso");
      }
    }
  }, [navigate]);

  useEffect(() => {
    const savedUser = localStorage.getItem("admin_user");
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
    fetchProfile();
  }, [fetchProfile]);

  const handleLogout = async () => {
    await authApi.logout();
    navigate("/panel-gestion-8k2m/acceso");
  };

  const handleProfileUpdate = (updatedUser) => {
    setUser(updatedUser);
  };

  const navigation = [
    { name: "Dashboard Propiedades", href: "/admin/dashboard", icon: LayoutDashboard },
    { name: "Mensajes", href: "/admin/mensajes", icon: MessageSquare },
    { name: "Blog", href: "/admin/blog", icon: FileText },
    { divider: true, label: "CRM" },
    { name: "Dashboard CRM", href: "/admin/crm", icon: Users },
    { name: "Analytics", href: "/admin/crm/analytics", icon: BarChart3 },
    { name: "Contactos", href: "/admin/crm/contacts", icon: Users },
    { name: "Equipo", href: "/admin/crm/team", icon: UserCog },
    { name: "Etapas", href: "/admin/crm/stages", icon: Layers },
    { divider: true, label: "Sistema" },
    { name: "Configuración", href: "/admin/settings", icon: Settings },
  ];

  const isActive = (href) => {
    if (href === "/admin/crm" && location.pathname === "/admin/crm") return true;
    if (href !== "/admin/crm" && location.pathname.startsWith(href)) return true;
    return location.pathname === href;
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Mobile sidebar toggle */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a] border-b border-white/5 p-4 flex items-center justify-between">
        <Link to="/admin/dashboard" className="text-[#D4AF37] font-semibold">
          Ikonic Deals Admin
        </Link>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="text-white"
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </Button>
      </div>

      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 z-40 h-screen w-64 bg-[#0a0a0a] border-r border-white/5 transform transition-transform lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-6 border-b border-white/5">
            <Link to="/admin/dashboard" className="flex items-center gap-2">
              <span className="text-[#D4AF37] font-semibold text-lg">Ikonic Deals</span>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navigation.map((item, idx) => {
              if (item.divider) {
                return (
                  <div key={idx} className="pt-4 pb-2">
                    <p className="text-[#A3A3A3] text-xs uppercase tracking-wider px-3">
                      {item.label}
                    </p>
                  </div>
                );
              }
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    isActive(item.href)
                      ? "bg-[#D4AF37]/10 text-[#D4AF37]"
                      : "text-[#A3A3A3] hover:text-white hover:bg-white/5"
                  }`}
                >
                  <item.icon size={18} />
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* User & Logout */}
          <div className="p-4 border-t border-white/5">
            <div 
              className="flex items-center gap-3 mb-4 px-3 py-2 rounded-lg cursor-pointer hover:bg-white/5 transition-colors"
              onClick={() => setShowProfileModal(true)}
              data-testid="profile-button"
            >
              <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                <span className="text-[#D4AF37] font-semibold text-sm">
                  {user?.name?.charAt(0)?.toUpperCase() || "A"}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">
                  {user?.name || "Admin"} {user?.last_name || ""}
                </p>
                <p className="text-[#A3A3A3] text-xs truncate">{user?.email}</p>
              </div>
              <Edit2 size={14} className="text-[#A3A3A3]" />
            </div>
            
            <div className="space-y-1">
              <Link
                to="/"
                className="flex items-center gap-3 px-3 py-2 rounded-lg text-[#A3A3A3] hover:text-white hover:bg-white/5 transition-colors"
              >
                <ChevronLeft size={18} />
                <span>Ver Sitio</span>
              </Link>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-[#A3A3A3] hover:text-red-500 hover:bg-red-500/5 transition-colors"
              >
                <LogOut size={18} />
                <span>Cerrar Sesión</span>
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        {/* Top bar with notifications (desktop only) */}
        <div className="hidden lg:flex items-center justify-end gap-4 px-6 py-4 border-b border-white/5 bg-[#0a0a0a]">
          <NotificationBell />
        </div>
        <div className="p-4 sm:p-6 lg:p-8">
          {children}
        </div>
      </main>

      {/* Profile Modal */}
      <ProfileModal 
        open={showProfileModal}
        onOpenChange={setShowProfileModal}
        user={user}
        onProfileUpdate={handleProfileUpdate}
      />
    </div>
  );
};

export default AdminLayout;
